"use client";

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { RefObject } from "react";
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}
interface TeamStackScrollOptions {
  sectionRef: RefObject<HTMLElement | null>;
  stageRef: RefObject<HTMLDivElement | null>;
  originRef: RefObject<HTMLDivElement | null>;
  stackRef: RefObject<HTMLDivElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
  outroRef: RefObject<HTMLDivElement | null>;
  cardSelector: string;
  count: number;
  onActiveChange: (index: number) => void;
  enabled?: boolean;
}

// Deck peek — upcoming cards fan slightly down/right behind the active card so
// the viewer can see there are more people underneath.
const PEEK_X = 6;
const PEEK_Y = 16;
const PEEK_SCALE = 0.05;
const PEEK_FADE = 0.16;
const MAX_PEEK = 3; // only this many cards peek at once

// How much the card shrinks as it's placed onto the bottom-right stack.
const LAND_SCALE = 0.55;

// Fixed hand-placed tilt so the landed deck looks physically stacked.
const TILT = [-6, 5, -4, 6, -5, 4];

// Resting transform for a card sitting `rel` positions behind the active one
// (rel 0 = active/front, full size).
const deckState = (rel: number) => {
  const r = Math.min(Math.max(rel, 0), MAX_PEEK);
  return {
    x: r * PEEK_X,
    y: r * PEEK_Y,
    scale: 1 - r * PEEK_SCALE,
    rotation: 0,
    autoAlpha: rel > MAX_PEEK ? 0 : 1 - r * PEEK_FADE
  };
};

/**
 * Drives the "Meet Our Team" interaction.
 *
 * Origin (top-left) holds a deck: the active card in front at full size,
 * upcoming members peeking behind it. Each scroll segment travels the active
 * card — one continuous, visible motion — into the growing bottom-right stack
 * (shrinking as it's "placed"), while the deck advances so the next member
 * slides to the front. Every card travels; once the last leaves, an outro
 * fades into the emptied top-left. The active index is reported via
 * onActiveChange (derived from the timeline's own time), and it flips mid-travel
 * so the info panel changes in parallel with the moving card.
 *
 * Timeline budget per card = 1 unit:
 *   [i,       i+0.62]  active card travels origin -> stack
 *   [i+0.45,  i+0.95]  deck behind advances one step (next member to front)
 */
export const useTeamStackScroll = t0 => {
  const $ = _c(16);
  const {
    sectionRef,
    stageRef,
    originRef,
    stackRef,
    panelRef,
    outroRef,
    cardSelector,
    count,
    onActiveChange,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== cardSelector || $[1] !== count || $[2] !== enabled || $[3] !== onActiveChange || $[4] !== originRef || $[5] !== outroRef || $[6] !== panelRef || $[7] !== stackRef || $[8] !== stageRef) {
    t2 = () => {
      if (!enabled || window.innerWidth < 1024) {
        return;
      }
      const cards = gsap.utils.toArray(cardSelector);
      if (!cards.length || !originRef.current || !stackRef.current) {
        return;
      }
      const getDelta = () => {
        const o = originRef.current.getBoundingClientRect();
        const s = stackRef.current.getBoundingClientRect();
        return {
          x: s.left + s.width / 2 - (o.left + o.width / 2),
          y: s.top + s.height / 2 - (o.top + o.height / 2)
        };
      };
      cards.forEach((card, i) => {
        gsap.set(card, {
          ...deckState(i),
          zIndex: count - i
        });
      });
      if (outroRef.current) {
        gsap.set(outroRef.current, {
          autoAlpha: 0,
          y: 30,
          scale: 0.96,
          filter: "blur(8px)"
        });
      }
      let current = -1;
      const setActive = i_0 => {
        if (i_0 !== current) {
          current = i_0;
          onActiveChange(i_0);
        }
      };
      const master = gsap.timeline({
        scrollTrigger: {
          trigger: stageRef.current,
          start: "top 8%",
          end: `+=${count * 1500}`,
          pin: true,
          scrub: 1.2,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: self => {
            const tl = self.animation as gsap.core.Timeline | undefined;
            const t = tl ? tl.time() : self.progress * count;
            setActive(Math.min(count - 1, Math.max(0, Math.round(t))));
          }
        }
      });
      cards.forEach((card_0, i_1) => {
        const tilt = TILT[i_1 % TILT.length];
        master.set(card_0, {
          zIndex: 100 + i_1
        }, i_1);
        master.to(card_0, {
          x: () => getDelta().x + i_1 * 3,
          rotation: tilt,
          duration: 0.62,
          ease: "power2.inOut"
        }, i_1);
        master.to(card_0, {
          y: () => getDelta().y + i_1 * 3,
          scale: LAND_SCALE,
          duration: 0.62,
          ease: "power2.in"
        }, i_1);
        for (let j = i_1 + 1; j < count; j++) {
          master.to(cards[j], {
            ...deckState(j - (i_1 + 1)),
            duration: 0.5,
            ease: "power3.out"
          }, i_1 + 0.45);
        }
      });
      if (panelRef.current) {
        master.to(panelRef.current, {
          autoAlpha: 0,
          y: -20,
          duration: 0.35,
          ease: "power2.in"
        }, count - 0.35);
      }
      if (outroRef.current) {
        master.to(outroRef.current, {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          filter: "blur(0px)",
          duration: 0.7,
          ease: "power3.out"
        }, count - 0.15);
        master.to({}, {
          duration: 0.5
        });
      }
      return () => {
        master.scrollTrigger?.kill();
        master.kill();
      };
    };
    $[0] = cardSelector;
    $[1] = count;
    $[2] = enabled;
    $[3] = onActiveChange;
    $[4] = originRef;
    $[5] = outroRef;
    $[6] = panelRef;
    $[7] = stackRef;
    $[8] = stageRef;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== count || $[11] !== enabled) {
    t3 = [enabled, count];
    $[10] = count;
    $[11] = enabled;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== sectionRef || $[14] !== t3) {
    t4 = {
      scope: sectionRef,
      dependencies: t3
    };
    $[13] = sectionRef;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  useGSAP(t2, t4);
};